import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useCert from '../hooks/useCert';
import { MEDIA_CODE, PAGE_NM } from '../types/types';
import { useBaseParam } from '../hooks/useBaseParam';
import { VALID_LENGTH } from './useStep1';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil';
import { validateRegistBackNo } from '../helper/validator';
import useBeacon from '../hooks/useBeacon';
import { beaconSelector, mtgSavePreReqTempSelector } from '../recoil/selector';
import { mutatePreReq } from '../../infra/api/mtg/mutatePreReq';
import { mutateSavePreRequest } from '../../infra/api/mtg/mutateSavePreRequest';
import useNav from '../hooks/useNav';
import useToast from '../hooks/useToast';
import useInterval from '../hooks/useInterval';

interface PopUpMessage {
  isOpen: boolean;
  popupMessage: string;
  onClickConfirm: () => void;
}

const useStep1Confirm = () => {
  const location: any = useLocation();
  const { media } = useParams();
  const isMtg = location?.state?.ref_page === PAGE_NM.MTG_CONFIRM;

  const minute = location?.state?.minute ?? 3;
  const second = location?.state?.second ?? 0;

  const [personal, setPersonal] = useRecoilState(Atom.personalInfo);
  const [hasTimePassed, setHasTimePassed] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number>(
    minute * 60 + second,
  );
  const [certNumber, setCertNumber] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const { reqCert, checkCertComp } = useCert(media, isMtg);
  const { navigate, routerNavigate, navigateHome } = useNav();
  const { addToast } = useToast();
  const [txSeqNo, setTxSeqNo] = useState<string | null>(null);
  const [residentNo, setResidentNo] = useState<string | null>(null);
  const [popup, setPopup] = useState<PopUpMessage>({
    isOpen: false,
    popupMessage: '',
    onClickConfirm: () => {},
  });

  useEffect(() => {
    if (location && txSeqNo == null) {
      setTxSeqNo(location.state?.txSeqNo ?? '');
      setResidentNo(location.state?.residentNo ?? '');
    }
  }, [location]);

  const checkReuseYn = () => {
    if (personal?.reuseYn === 'N' || !personal?.reuseYn) {
      setPersonal((prev: any) => {
        return {
          ...prev,
          objectTyp: '',
          hopefulAmt: '',
          salaryAmt: '',
          jobTyp: {},
          joinDt: '',
          employeeTyp: {},
          businessTyp: {},
          houseTyp: '',
          compRegNo: '',
          compNm: '',
          healthPayerTyp: '01',
          carOwnerYn: '',
          carNo: '',
          monthlyYn: '',
          regDate: '',
          reuseYn: '',
        };
      });
    }
  };

  const wrappedReqCert = useBaseParam(
    reqCert,
    {
      regidentNo: residentNo,
      certRequestTypeCd: '02',
    },
    { noToast: true },
  );

  const requestCert = async () => {
    const result: any = await wrappedReqCert();
    if (result?.data?.result?.txSeqNo) {
      setTxSeqNo(result?.data?.result?.txSeqNo);
      setPopup({
        isOpen: true,
        popupMessage: `인증번호가 재전송 됐어요.\n최대 5회 전송 가능하며\n초과시, 메인으로 이동합니다.\n(${
          5 - result.data.result.validCnt
        }/5)`,
        onClickConfirm: () => {
          closePopup();
        },
      });
      return true;
    } else if (result.data?.resultCode === 'E042') {
      setTxSeqNo('');
      setPopup({
        isOpen: true,
        popupMessage:
          '인증번호 요청 횟수를 초과했습니다.\n처음으로 돌아갑니다.',
        onClickConfirm: () => {
          closePopup();
          navigateHome();
        },
      });
      return false;
    } else {
      addToast(result?.data?.resultMessage ?? '인증번호 요청에 실패했어요.');
      return false;
    }
  };

  const onClickRequestAgain = async () => {
    clearNumber();
    const result = await requestCert();
    if (result) {
      setRemainingTime(minute * 60 + second);
    }
  };

  const wrappedCheckCertComp = useBaseParam(
    checkCertComp,
    {
      txSeqNo,
      mediaCd: media ? MEDIA_CODE[media.toUpperCase()] : '',
      optNo: certNumber,
    },
    { noToast: true },
  );

  const changeSecondsToMinutes = (seconds: number) => {
    let minutes =
      Math.floor(seconds / 60) < 10
        ? '0' + Math.floor(seconds / 60)
        : Math.floor(seconds / 60);
    let remainingSeconds =
      seconds % 60 < 10 ? '0' + (seconds % 60) : seconds % 60;

    return `${minutes}:${remainingSeconds}`;
  };

  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: isMtg
      ? PAGE_NM.MTG_SSN_CONFIRM
      : PAGE_NM.CONDITIONS_STEP1_CONFIRM,
  });

  const sendBeaconCertSellSucc = () => {
    beacon.sendCertCellSucc();
  };
  const sendBeaconCertCellFail = () => {
    beacon.sendCertCellFail();
  };
  const sendBeaconCertCellWait = () => {
    beacon.sendCertCellWait();
  };

  const [mInfo] = useRecoilState(Atom.mortgageInfo);
  const applyPreReq = useBaseParam(mutatePreReq, {
    preRequestSeq: mInfo.preRequestSeq,
  });

  const mtgSavePreReqTemp = useRecoilValue(mtgSavePreReqTempSelector);

  const putInputInfo = useBaseParam(mutateSavePreRequest, {
    ...mtgSavePreReqTemp,

    hopefulAmt: mtgSavePreReqTemp.hopefulAmt
      ? mtgSavePreReqTemp.hopefulAmt * 10000
      : mtgSavePreReqTemp.hopefulAmt,
    previousAmt: mtgSavePreReqTemp.previousAmt
      ? mtgSavePreReqTemp.previousAmt * 10000
      : mtgSavePreReqTemp.previousAmt,
    tenantAmt: mtgSavePreReqTemp.tenantAmt
      ? mtgSavePreReqTemp.tenantAmt * 10000
      : mtgSavePreReqTemp.tenantAmt,
    salaryAmt: mtgSavePreReqTemp.salaryAmt
      ? mtgSavePreReqTemp.salaryAmt * 10000
      : mtgSavePreReqTemp.salaryAmt,
    houseAmt: mtgSavePreReqTemp.housePrice
      ? mtgSavePreReqTemp.housePrice * 10000
      : mtgSavePreReqTemp.housePrice,

    disposalPlanYn:
      mtgSavePreReqTemp.disposalPlanYn === ''
        ? null
        : mtgSavePreReqTemp.disposalPlanYn,
    previousYn:
      mtgSavePreReqTemp.previousYn === '' ? null : mtgSavePreReqTemp.previousYn,
    tenantYn:
      mtgSavePreReqTemp.tenantYn === '' ? null : mtgSavePreReqTemp.tenantYn,
    firstYn:
      mtgSavePreReqTemp.firstYn === '' ? null : mtgSavePreReqTemp.firstYn,
    openDt: mtgSavePreReqTemp.openDt === '' ? null : mtgSavePreReqTemp.openDt,
  });

  const saveMtgPreReq = async () => {
    return await putInputInfo();
  };

  const saveInputInfo = async () => {
    const response = await saveMtgPreReq();
    if (response.data.isSucceed) {
      const result = await applyPreReq();
      return result.data.isSucceed;
    }
    return false;
  };

  const clearNumber = () => {
    setCertNumber('');
  };

  const onChangeCertNumberInput = (value: string) => {
    if (value.length > 6) return;
    setCertNumber(value);
    if (!hasTimePassed) setIsButtonDisabled(value.length < 6);
  };

  const closePopup = () => {
    setPopup({
      ...popup,
      isOpen: false,
    });
  };

  const onClickConfirmButton = async () => {
    const res = await wrappedCheckCertComp();
    if (res?.data?.isSucceed === true) {
      sendBeaconCertSellSucc();
      checkReuseYn();
      if (location?.state) {
        delete location.state.residentNo;
        delete location.state.txSeqNo;
      }

      if (location?.state?.ref_page === PAGE_NM.MTG_CONFIRM) {
        await saveInputInfo();
        navigate('/mtg/loading', { state: location?.state });
      } else {
        navigate('/conditions/3', { state: location?.state });
      }
    } else {
      clearNumber();
      sendBeaconCertCellFail();
      const validCnt = 3 - (res?.data?.result?.validCnt ?? 1);
      if (res?.data?.result.resultCode === 'E047') {
        setPopup({
          isOpen: true,
          popupMessage:
            '19세 미만일 경우 사용이 불가능합니다.\n처음 화면으로 이동합니다.',
          onClickConfirm: () => {
            navigateHome();
          },
        });
      } else if (res?.data?.result.resultCode === 'B131') {
        if (validCnt == 0) {
          setPopup({
            isOpen: true,
            popupMessage: `인증 횟수를 초과 했어요.\n입력 정보를 다시 확인 해주세요\n(${validCnt}/3)`,
            onClickConfirm: () => {
              closePopup();
            },
          });
        } else {
          setPopup({
            isOpen: true,
            popupMessage: `인증번호가 맞지 않아요.\n다시 확인 해주세요\n(${validCnt}/3)`,
            onClickConfirm: () => {
              closePopup();
            },
          });
        }
      } else {
        setPopup({
          isOpen: true,
          popupMessage: `본인 인증에 실패했어요.\n다시 확인 해주세요.`,
          onClickConfirm: () => {
            closePopup();
            routerNavigate(-1);
          },
        });
      }
    }
  };

  const clearRemainingTimeInterval = useInterval(() => {
    setRemainingTime((prev) => {
      const value = prev - 1;
      if (value < 0) return 0;
      return value;
    });
  }, 1000);

  useEffect(() => {
    if (remainingTime === 0) {
      setHasTimePassed(true);
      setIsButtonDisabled(true);
    } else {
      setHasTimePassed(false);
    }
    changeSecondsToMinutes(remainingTime);
  }, [remainingTime, clearRemainingTimeInterval]);

  useEffect(() => {
    return () => {
      if (clearRemainingTimeInterval) {
        clearRemainingTimeInterval();
      }
    };
  }, []);

  return {
    hasTimePassed,
    remainingTime,
    certNumber,
    isButtonDisabled,
    popup,

    onClickRequestAgain,
    onClickConfirmButton,
    onChangeCertNumberInput,
    changeSecondsToMinutes,
  };
};

export default useStep1Confirm;
